import { Check, ArrowRight } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { scrollToId } from '../lib/smoothScroll';

const capabilities = [
  'Centralised downtime management — see which vehicle has been off road longest, at a click',
  'Service and MOT reminders kept on track automatically',
  'SLA-driven queues and intelligent prioritisation',
  'Automated updates, confirmations and reminders',
  'Complete vehicle history and audit trail',
  'Human support for exceptions that need judgement',
];

export const DarkProduct = () => (
  <section id="platform" className="scroll-mt-20 bg-[#0B1F1A] py-20 md:py-28" data-testid="platform-section">
    <div className="container-relay grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-10">
      <Reveal className="lg:col-span-5">
        <p className="eyebrow-dark mb-4" data-testid="platform-eyebrow">Built for real operations</p>
        <h2 className="text-3xl font-bold tracking-tight text-[#FAFAF7] sm:text-4xl" data-testid="platform-headline">
          Automate the update.
          <br />
          Escalate the exception.
        </h2>
        <p className="mt-5 max-w-[46ch] text-base leading-relaxed text-[#9DB4AA]" data-testid="platform-body">
          Relay reduces the need to copy, chase and re-enter information across calls, emails and systems — so
          vehicles spend less time off the road, service reminders never slip, and downtime is managed
          consistently, not reactively.
        </p>
        <div className="mt-8 rounded-xl border border-[#1E3A31] bg-[#0E261F] p-5" data-testid="downtime-stat">
          <p className="font-op text-[10px] font-medium uppercase tracking-[0.22em] text-[#7FBFA8]">
            The cost of waiting
          </p>
          <p className="mt-2.5 text-sm leading-relaxed text-[#E5EDE8]">
            Industry research puts a single van off the road at{' '}
            <span className="font-semibold text-[#FAFAF7]">£600–£1,100+ per day</span>. Downtime is a full-time
            job — usually pieced together from phone calls, weekly reports and spreadsheets. Relay makes it one
            view: the longest off-road vehicles surfaced first, ready to chase.
          </p>
        </div>
        <ul className="mt-8 space-y-4" data-testid="platform-capabilities">
          {capabilities.map((c) => (
            <li key={c} className="flex items-start gap-3.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#163229]">
                <Check size={12} strokeWidth={2.5} className="text-[#7FBFA8]" />
              </span>
              <span className="text-sm font-medium leading-relaxed text-[#E5EDE8]">{c}</span>
            </li>
          ))}
        </ul>
        <button data-testid="platform-cta" onClick={() => scrollToId('enquiry')} className="btn-ghost-dark mt-10">
          Explore the platform <ArrowRight size={16} />
        </button>
      </Reveal>
      <div className="relative lg:col-span-7">
        <Reveal delay={0.15}>
          <div className="relative mx-auto max-w-[560px]">
            <div className="overflow-hidden rounded-xl border border-[#1E3A31] shadow-[0_40px_80px_-32px_rgba(0,0,0,0.6)]">
              <img
                src="/assets/relay-confirmations-full.webp"
                alt="Relay booking confirmations screen for uploading garage confirmation emails and matching them to VDA requests"
                loading="lazy"
                decoding="async"
                className="block h-auto w-full"
                data-testid="platform-screenshot-confirmations"
              />
            </div>
            <div className="absolute -bottom-12 -right-3 w-[42%] sm:-right-8">
              <img
                src="/assets/relay-phone.png"
                alt="Relay mobile operational dashboard with live request counts and the prioritised queue"
                loading="lazy"
                decoding="async"
                className="block h-auto w-full drop-shadow-[0_32px_44px_rgba(0,0,0,0.5)]"
                data-testid="platform-phone"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
